import { Dispatch, SetStateAction, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { OperationIncident, updateOperationIncident } from '@networkRequest/operationsService';
import { useUser } from '@hooks/useUser';
import LocationFields from '@pages/Operations/CreateOperationPage/components/LocationFields';
import { PrimaryButton, SecondaryButton } from '@components/index';

const defaultFormErrors = {
  name: null,
  street_address_1: null,
  street_address_2: null,
  city: null,
  state: null,
  zip: null,
};

const EditIncidentLocationDialog = ({
  open,
  onClose,
  incident,
  setIncident,
  setError
}: {
  open: boolean;
  onClose: () => void,
  incident: OperationIncident;
  setIncident: Dispatch<SetStateAction<OperationIncident | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}) => {
  const { user } = useUser();

  if (!user) return null;

  const defaultFormData = {
    name: incident?.location?.name ?? '',
    street_address_1: incident?.location?.street_address_1 ?? '',
    street_address_2: incident?.location?.street_address_2 ?? '',
    city: incident?.location?.city ?? incident?.location?.city ?? '',
    state: incident?.location?.state ?? '',
    zip: incident?.location?.zip ?? '',
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [formErrors, setFormErrors] = useState(defaultFormErrors);

  const hasErrors = !Object.values(formErrors).every(value => value === null);

  const handleCancel = () => {
    setFormData(defaultFormData);
    onClose();
  };

  const validateForm = () => {
    const { street_address_2, ...rest } = formData;
    const newErrors = { ...defaultFormErrors };
    Object.keys(rest).forEach((field) => {
      newErrors[field] = rest[field]?.length ? null : `${field} is required`
    })

    setFormErrors(newErrors);
  };

  const handleLocationChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    validateForm();
    const { data, error } = await updateOperationIncident({
      accessToken: user.accessToken,
      incidentId: incident.id,
      payload: {
        location_attributes: formData,
      }
    });

    if (data) {
      setIncident(data);
    } else {
      setError(error ?? 'Failed to update incident location');
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8, width: '783px' } }
      }}
    >
      <DialogTitle sx={{ padding: 3 }} variant='h5'>
        Edit Incident Location
      </DialogTitle>
      <DialogContent sx={{ paddingTop: '16px !important' }}>
        <LocationFields
          name={formData.name ?? ''}
          street_address_1={formData.street_address_1 ?? ''}
          street_address_2={formData.street_address_2 ?? ''}
          city={formData.city ?? ''}
          state={formData.state ?? ''}
          zip={formData.zip ?? ''}
          onChange={handleLocationChange}
          onBlur={validateForm}
          showDelete={false}
          formErrors={formErrors}
        />
      </DialogContent>
      <DialogActions
        sx={{
          paddingTop: 1,
          paddingBottom: 3,
          paddingRight: 3,
          paddingLeft: 3,
        }}
      >
        <SecondaryButton action={handleCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type='submit'
          action={handleSubmit}
          disabled={hasErrors}
        >
          Submit
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditIncidentLocationDialog;